import { useState } from "react";
import { useMediaQuery } from "@mui/material";
import { Icon } from "../components/liquid/LiquidPrimitives";

interface FaqItem {
    id: string;
    pregunta: string;
    respuesta: string;
}

interface FaqCategoria {
    titulo: string;
    items: FaqItem[];
}

const faqData: FaqCategoria[] = [
    {
        titulo: "Equipos y Servicios",
        items: [
            {
                id: "eq1",
                pregunta: "¿Los equipos se venden?",
                respuesta:
                    "Los equipos se entregan en comodato (alquiler). El abono se paga mensualmente e incluye los servicios de instalación, mantenimiento y cambio de filtros.",
            },
            {
                id: "eq2",
                pregunta: "¿Cómo es la conexión?",
                respuesta:
                    "El equipo se conecta a una fuente de agua cercana, realizamos un bypass de alguna canilla SIN anular nada. Utilizamos una válvula y manguera de 1/4 pulgada.",
            },
            {
                id: "eq3",
                pregunta: "¿Cuando instalan cortan alguna canilla?",
                respuesta:
                    "Nuestros técnicos no anulan ninguna canilla. Hacemos un bypass de una toma de agua y conectamos los equipos desde ahí.",
            },
            {
                id: "eq4",
                pregunta: "¿Cuánto duran los filtros?",
                respuesta:
                    "La duración de los filtros depende del uso que se le dé al equipo. Normalmente una familia tipo con un consumo habitual tendría que cambiar los filtros cada 24 meses.",
            },
            {
                id: "eq5",
                pregunta: "¿Si el agua no es potable puedo contratar el servicio?",
                respuesta:
                    "Nuestros equipos purifican el agua, NO la potabilizan. Para potabilizar agua deben contactarse con uno de nuestros representantes.",
            },
            {
                id: "eq6",
                pregunta: "¿Cómo se contactan con uno de sus representantes?",
                respuesta:
                    "A través de la línea telefónica: 0800-147-8632 o vía WhatsApp al 11 3634-5665.",
            },
        ],
    },
    {
        titulo: "Servicio Técnico",
        items: [
            {
                id: "st1",
                pregunta: "¿Qué pasa si no sale agua caliente de mi equipo?",
                respuesta:
                    "Verificá que el equipo esté conectado al tomacorriente y que la luz roja de encendido esté encendida. Asegurate de que la válvula de seguridad esté activada. Si el problema persiste, comunicate con nuestro soporte técnico al 11 3889-9763.",
            },
            {
                id: "st2",
                pregunta: "¿Qué pasa si mi equipo pierde agua?",
                respuesta:
                    "Desenchufá el equipo del tomacorriente. Cerrá la llave de paso azul ubicada detrás del equipo y comunicáte con nuestro servicio técnico.",
            },
        ],
    },
    {
        titulo: "Formas de Pago",
        items: [
            {
                id: "fp1",
                pregunta: "¿Cuáles son los métodos de pago?",
                respuesta:
                    "Débito automático, Tarjetas de Crédito Visa, Mastercard, American Express, Mercado Pago y Pago Fácil.",
            },
        ],
    },
    {
        titulo: "Mantenimiento",
        items: [
            {
                id: "mn1",
                pregunta: "¿Cada cuánto hacen los mantenimientos?",
                respuesta: "Los mantenimientos preventivos se realizan cada 4 a 6 meses.",
            },
            {
                id: "mn2",
                pregunta:
                    "¿Qué pasa si no se realiza el mantenimiento preventivo con la frecuencia habitual?",
                respuesta:
                    "Los equipos soportan períodos extensos sin mantenimiento. El hecho de no realizarlo con la frecuencia habitual no altera en lo absoluto la calidad de filtrado. Sin embargo, recomendamos respetar los intervalos para garantizar el óptimo funcionamiento del equipo a largo plazo.",
            },
        ],
    },
];

function AccordionItem({
    item,
    open,
    onToggle,
}: {
    item: FaqItem;
    open: boolean;
    onToggle: () => void;
}) {
    return (
        <div
            style={{
                borderBottom: "1px solid rgba(191,199,210,0.35)",
            }}
        >
            <button
                type="button"
                onClick={onToggle}
                style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 16,
                    padding: "18px 0",
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                    textAlign: "left",
                }}
            >
                <span
                    style={{
                        fontSize: 15,
                        fontWeight: 600,
                        letterSpacing: "-0.01em",
                        color: open ? "#00629c" : "var(--ink-900)",
                        lineHeight: 1.4,
                        transition: "color 0.2s",
                    }}
                >
                    {item.pregunta}
                </span>
                <span
                    style={{
                        flexShrink: 0,
                        width: 28,
                        height: 28,
                        borderRadius: 999,
                        background: open ? "#00629c" : "rgba(0,98,156,0.08)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: open ? "#ffffff" : "#00629c",
                        transition: "background 0.2s, transform 0.2s",
                        transform: open ? "rotate(45deg)" : "none",
                    }}
                >
                    <Icon.plus />
                </span>
            </button>

            {open && (
                <div
                    style={{
                        paddingBottom: 20,
                        fontSize: 14,
                        lineHeight: 1.8,
                        color: "var(--ink-500)",
                        maxWidth: 680,
                    }}
                >
                    {item.respuesta}
                </div>
            )}
        </div>
    );
}

export default function FAQsPage() {
    const isSmall = useMediaQuery("(max-width:900px)");
    const [openId, setOpenId] = useState<string | null>(null);

    const handleToggle = (id: string) => {
        setOpenId((prev) => (prev === id ? null : id));
    };

    return (
        <main style={{ position: "relative", zIndex: 2 }}>
            {/* Header */}
            <section style={{ padding: isSmall ? "16px 16px 0" : "24px 32px 0" }}>
                <div
                    style={{
                        borderRadius: isSmall ? 24 : 32,
                        background:
                            "linear-gradient(130deg, #001f33 0%, #003A5C 45%, #005A8C 100%)",
                        padding: isSmall ? "40px 28px" : "56px 72px",
                        position: "relative",
                        overflow: "hidden",
                    }}
                >
                    <div
                        style={{
                            position: "absolute",
                            inset: 0,
                            pointerEvents: "none",
                            backgroundImage:
                                "linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)",
                            backgroundSize: "48px 48px",
                        }}
                    />
                    <div style={{ position: "relative" }}>
                        <div
                            style={{
                                fontSize: 11,
                                fontWeight: 700,
                                letterSpacing: "0.16em",
                                textTransform: "uppercase",
                                color: "#cfe5ff",
                                marginBottom: 12,
                            }}
                        >
                            Preguntas frecuentes
                        </div>
                        <h1
                            style={{
                                fontSize: isSmall ? 34 : 52,
                                fontWeight: 700,
                                letterSpacing: "-0.03em",
                                lineHeight: 1.05,
                                color: "#ffffff",
                                margin: 0,
                            }}
                        >
                            ¿En qué podemos ayudarte?
                        </h1>
                        <p
                            style={{
                                fontSize: 15,
                                color: "rgba(255,255,255,0.55)",
                                marginTop: 14,
                                lineHeight: 1.6,
                                maxWidth: 440,
                            }}
                        >
                            Encontrá respuestas sobre nuestros equipos, instalación, pagos y
                            mantenimiento.
                        </p>
                    </div>
                </div>
            </section>

            {/* Contenido */}
            <section style={{ padding: isSmall ? "32px 16px 64px" : "48px 32px 80px" }}>
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: isSmall ? "1fr" : "220px 1fr",
                        gap: isSmall ? 32 : 56,
                        alignItems: "start",
                    }}
                >
                    {/* Nav lateral — solo desktop */}
                    {!isSmall && (
                        <nav style={{ position: "sticky", top: 100 }}>
                            <div
                                style={{
                                    fontSize: 11,
                                    fontWeight: 700,
                                    letterSpacing: "0.12em",
                                    textTransform: "uppercase",
                                    color: "var(--ink-400)",
                                    marginBottom: 12,
                                }}
                            >
                                Categorías
                            </div>
                            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                {faqData.map((cat) => (
                                    <a
                                        key={cat.titulo}
                                        href={`#${cat.titulo.toLowerCase().replace(/\s+/g, "-")}`}
                                        style={{
                                            padding: "8px 12px",
                                            borderRadius: 10,
                                            fontSize: 13,
                                            fontWeight: 500,
                                            color: "var(--ink-700)",
                                            textDecoration: "none",
                                            display: "block",
                                            transition: "background 0.15s",
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.background =
                                                "rgba(0,98,156,0.06)";
                                            e.currentTarget.style.color = "#00629c";
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.background = "transparent";
                                            e.currentTarget.style.color = "var(--ink-700)";
                                        }}
                                    >
                                        {cat.titulo}
                                    </a>
                                ))}
                            </div>

                            {/* Contacto */}
                            <div
                                style={{
                                    marginTop: 32,
                                    padding: "18px 16px",
                                    borderRadius: 16,
                                    background: "rgba(0,98,156,0.06)",
                                    border: "1px solid rgba(0,98,156,0.12)",
                                }}
                            >
                                <div
                                    style={{
                                        fontSize: 13,
                                        fontWeight: 600,
                                        color: "var(--ink-900)",
                                        marginBottom: 8,
                                    }}
                                >
                                    ¿No encontrás tu respuesta?
                                </div>
                                <div
                                    style={{
                                        fontSize: 12,
                                        color: "var(--ink-500)",
                                        lineHeight: 1.6,
                                        marginBottom: 12,
                                    }}
                                >
                                    Contactanos directamente y te ayudamos.
                                </div>
                                <a
                                    href="https://wa.me/5491136345665"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 6,
                                        padding: "8px 14px",
                                        borderRadius: 999,
                                        background: "#00629c",
                                        color: "#ffffff",
                                        fontSize: 12,
                                        fontWeight: 600,
                                        textDecoration: "none",
                                    }}
                                >
                                    <Icon.whatsapp /> WhatsApp
                                </a>
                            </div>
                        </nav>
                    )}

                    {/* FAQs */}
                    <div style={{ display: "flex", flexDirection: "column", gap: 40 }}>
                        {faqData.map((categoria) => (
                            <div
                                key={categoria.titulo}
                                id={categoria.titulo.toLowerCase().replace(/\s+/g, "-")}
                            >
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 10,
                                        marginBottom: 4,
                                    }}
                                >
                                    <div
                                        style={{
                                            width: 4,
                                            height: 18,
                                            borderRadius: 2,
                                            background: "#00629c",
                                            flexShrink: 0,
                                        }}
                                    />
                                    <h2
                                        style={{
                                            fontSize: 13,
                                            fontWeight: 700,
                                            letterSpacing: "0.10em",
                                            textTransform: "uppercase",
                                            color: "#00629c",
                                            margin: 0,
                                        }}
                                    >
                                        {categoria.titulo}
                                    </h2>
                                </div>

                                <div
                                    style={{
                                        borderRadius: 20,
                                        background: "rgba(255,255,255,0.80)",
                                        border: "1px solid rgba(191,199,210,0.35)",
                                        backdropFilter: "blur(16px)",
                                        padding: "0 24px",
                                    }}
                                >
                                    {categoria.items.map((item) => (
                                        <AccordionItem
                                            key={item.id}
                                            item={item}
                                            open={openId === item.id}
                                            onToggle={() => handleToggle(item.id)}
                                        />
                                    ))}
                                </div>
                            </div>
                        ))}

                        {/* Contacto mobile */}
                        {isSmall && (
                            <div
                                style={{
                                    padding: "24px 20px",
                                    borderRadius: 20,
                                    background: "rgba(0,98,156,0.06)",
                                    border: "1px solid rgba(0,98,156,0.12)",
                                    textAlign: "center",
                                }}
                            >
                                <div
                                    style={{
                                        fontSize: 15,
                                        fontWeight: 600,
                                        color: "var(--ink-900)",
                                        marginBottom: 8,
                                    }}
                                >
                                    ¿No encontrás tu respuesta?
                                </div>
                                <div
                                    style={{
                                        fontSize: 13,
                                        color: "var(--ink-500)",
                                        lineHeight: 1.6,
                                        marginBottom: 16,
                                    }}
                                >
                                    Contactanos directamente y te ayudamos.
                                </div>
                                <div
                                    style={{
                                        display: "flex",
                                        gap: 10,
                                        justifyContent: "center",
                                        flexWrap: "wrap",
                                    }}
                                >
                                    <a
                                        href="https://wa.me/5491136345665"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: 6,
                                            padding: "10px 20px",
                                            borderRadius: 999,
                                            background: "#00629c",
                                            color: "#ffffff",
                                            fontSize: 13,
                                            fontWeight: 600,
                                            textDecoration: "none",
                                        }}
                                    >
                                        <Icon.whatsapp /> WhatsApp
                                    </a>
                                    <a
                                        href="tel:08001478632"
                                        style={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: 6,
                                            padding: "10px 20px",
                                            borderRadius: 999,
                                            background: "transparent",
                                            color: "var(--ink-700)",
                                            fontSize: 13,
                                            fontWeight: 600,
                                            textDecoration: "none",
                                            border: "1px solid rgba(0,0,0,0.12)",
                                        }}
                                    >
                                        <Icon.phone /> 0800-147-8632
                                    </a>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </section>
        </main>
    );
}
